// Real OpenStreetMap-based map using MapLibre GL JS + OpenFreeMap tiles.
//
// Tiles: https://openfreemap.org/ — free, no API key, OSM-derived vector tiles.
// Style: 'liberty' is closest to the warm/paper aesthetic; 'positron' is light.
//
// The pin layer is a custom layer that mirrors PaperMap's category palette
// and pulse animation so the live-dot feel survives the move to real maps.

import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from "react";
import maplibregl, { type LngLatLike, type Map as MlMap } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { MA_CATS, type CategoryId } from "@myams/shared";

export interface MapPin {
  id: string;
  title?: string;
  lat: number;
  lng: number;
  cat?: CategoryId | null;
  live?: boolean | null;
}

interface Props {
  pins: MapPin[];
  center?: [number, number];      // [lng, lat]
  zoom?: number;
  bounds?: maplibregl.LngLatBoundsLike;
  style?: React.CSSProperties;
  interactive?: boolean;
  onPinClick?: (pin: MapPin) => void;
  /** Fires when the user clicks the map background (not a pin). */
  onMapClick?: () => void;
  fitToPins?: boolean;
  highlight?: string | null;
}

export interface MapViewHandle {
  zoomIn: () => void;
  zoomOut: () => void;
  locate: () => void;
  flyTo: (lng: number, lat: number, zoom?: number) => void;
}

// Amsterdam Centraal as the natural default center.
const DEFAULT_CENTER: [number, number] = [4.9041, 52.3791];
const TILE_STYLE = "https://tiles.openfreemap.org/styles/liberty";

// Resolve a CSS variable from :root into a real color string so it can be
// fed into Canvas / inline SVG markers (MapLibre markers don't inherit CSS vars).
function readCssVar(name: string, fallback = "#1a1208"): string {
  if (typeof window === "undefined") return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
}

const CAT_COLOR_VAR: Record<string, string> = {
  music: "--ma-brick",
  food: "--ma-mustard",
  art: "--ma-canal",
  market: "--ma-mustard",
  cinema: "--ma-pink",
  outdoor: "--ma-canal",
  cafe: "--ma-mustard",
  queer: "--ma-pink",
  sports: "--ma-sky",
  hidden: "--ma-ink",
  all: "--ma-ink",
};

export const MapView = forwardRef<MapViewHandle, Props>(function MapView({
  pins,
  center = DEFAULT_CENTER,
  zoom = 12.5,
  bounds,
  style,
  interactive = true,
  onPinClick,
  onMapClick,
  fitToPins = false,
  highlight = null,
}, ref) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MlMap | null>(null);
  const markersRef = useRef<Map<string, maplibregl.Marker>>(new Map());
  // Separate ref for the "you are here" pulse — it's not a pin in the
  // pins set and shouldn't get the highlight/scale treatment that the
  // pin sync effect applies.
  const userMarkerRef = useRef<maplibregl.Marker | null>(null);

  const paper = useMemo(() => readCssVar("--ma-paper", "#f0e4cc"), []);
  const ink = useMemo(() => readCssVar("--ma-ink", "#1a1208"), []);

  // Initialize map once.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: TILE_STYLE,
      center: center as LngLatLike,
      zoom,
      attributionControl: { compact: true },
      interactive,
      dragRotate: false,
      pitchWithRotate: false,
    });
    map.touchZoomRotate.disableRotation();
    mapRef.current = map;

    // The OpenFreeMap "liberty" style references a sprite sheet with
    // dozens of POI icons (office, gate, swimming_pool, ferry_terminal,
    // bollard, …). We don't load that sprite — we use custom DOM
    // markers — so MapLibre warns once per missing icon and floods the
    // console. Injecting a 1×1 transparent placeholder for any
    // requested icon silences the warnings without affecting render:
    // the icon "exists" as far as MapLibre's check is concerned, but
    // it's a transparent pixel so nothing visible changes.
    const onMissingImage = (e: { id: string }) => {
      if (!map.hasImage(e.id)) {
        map.addImage(e.id, { width: 1, height: 1, data: new Uint8Array([0, 0, 0, 0]) });
      }
    };
    map.on("styleimagemissing", onMissingImage);

    // Click on the map background (not a pin). MapLibre fires this for
    // canvas clicks; marker clicks are DOM events that don't bubble here.
    const handler = () => onMapClickRef.current?.();
    map.on("click", handler);

    return () => {
      map.off("click", handler);
      map.off("styleimagemissing", onMissingImage);
      map.remove();
      mapRef.current = null;
      // map.remove() detaches all markers' DOM, but the ref would still
      // point at a stale Marker instance — clear it so the next locate()
      // creates a fresh marker on the new map.
      userMarkerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the latest onMapClick callback in a ref so we don't have to
  // recreate the map when the handler identity changes.
  const onMapClickRef = useRef(onMapClick);
  useEffect(() => { onMapClickRef.current = onMapClick; }, [onMapClick]);

  // Expose imperative controls to parent (zoom buttons, geolocate, etc.).
  useImperativeHandle(ref, () => ({
    zoomIn: () => mapRef.current?.zoomIn({ duration: 300 }),
    zoomOut: () => mapRef.current?.zoomOut({ duration: 300 }),
    locate: () => {
      if (!navigator.geolocation) return;
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const map = mapRef.current;
          if (!map) return;
          const lngLat: [number, number] = [pos.coords.longitude, pos.coords.latitude];
          // Drop or move the "you are here" marker. Distinct from venue/
          // event pins: theme-coloured sky-blue with a white halo and the
          // same maMapPulse ripple, so it reads as live without being
          // confused for a venue dot.
          if (userMarkerRef.current) {
            userMarkerRef.current.setLngLat(lngLat);
          } else {
            const el = buildUserLocationElement();
            userMarkerRef.current = new maplibregl.Marker({ element: el, anchor: "center" })
              .setLngLat(lngLat)
              .addTo(map);
          }
          map.flyTo({ center: lngLat, zoom: 14, duration: 800 });
        },
        () => { /* user denied or no position — silently no-op */ },
        { enableHighAccuracy: true, timeout: 8000 }
      );
    },
    flyTo: (lng, lat, z) => mapRef.current?.flyTo({ center: [lng, lat], zoom: z ?? 15, duration: 800 }),
  }), []);

  // Sync pins.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    // Defensive filter: any pin with non-finite coords would cause
    // MapLibre to throw "Expected value to be of type number, but
    // found null instead" once it hits Math.min / fitBounds. Callers
    // *should* pre-filter; we belt-and-suspender here so a missed
    // filter upstream doesn't take the whole map down.
    const safePins = pins.filter(
      (p) => Number.isFinite(p.lat) && Number.isFinite(p.lng)
    );

    const existing = markersRef.current;
    const next = new Set(safePins.map((p) => p.id));

    // Remove any markers for pins no longer present.
    for (const [id, m] of existing) {
      if (!next.has(id)) { m.remove(); existing.delete(id); }
    }

    for (const p of safePins) {
      const cur = existing.get(p.id);
      if (cur) {
        cur.setLngLat([p.lng, p.lat]);
        continue;
      }
      const colorVar = CAT_COLOR_VAR[p.cat ?? "all"] ?? "--ma-ink";
      const color = readCssVar(colorVar, "#1a1208");
      const el = buildMarkerElement({ color, paper, ink, live: !!p.live, highlight: highlight === p.id });
      const marker = new maplibregl.Marker({ element: el, anchor: "center" })
        .setLngLat([p.lng, p.lat])
        .addTo(map);
      if (p.title) marker.setPopup(new maplibregl.Popup({ offset: 16, closeButton: false }).setText(p.title));
      if (onPinClick) {
        el.addEventListener("click", (e) => { e.stopPropagation(); onPinClick(p); });
        el.style.cursor = "pointer";
      }
      existing.set(p.id, marker);
    }

    // React to highlight: scale up + ring on the matching marker. IMPORTANT:
    // don't touch the marker el's own `transform` — MapLibre uses it to
    // position the marker via translate(). Apply the scale to the inner
    // dot element instead.
    for (const [id, m] of existing) {
      const el = m.getElement() as HTMLDivElement;
      const dot = el.firstChild as HTMLDivElement | null;
      const on = id === highlight;
      el.style.zIndex = on ? "10" : "1";
      if (dot) {
        dot.style.transition = "transform 200ms ease, box-shadow 200ms ease";
        dot.style.transform = on ? "scale(1.4)" : "scale(1)";
        dot.style.boxShadow = on
          ? `0 0 0 2px ${ink}, 0 2px 6px rgba(0,0,0,0.25)`
          : "0 2px 6px rgba(0,0,0,0.25)";
      }
    }

    if (fitToPins && safePins.length > 0) {
      const lngs = safePins.map((p) => p.lng);
      const lats = safePins.map((p) => p.lat);
      map.fitBounds(
        [
          [Math.min(...lngs), Math.min(...lats)],
          [Math.max(...lngs), Math.max(...lats)],
        ],
        { padding: 60, maxZoom: 14, duration: 600 }
      );
    } else if (bounds) {
      map.fitBounds(bounds, { padding: 40, duration: 0 });
    }
  }, [pins, fitToPins, bounds, onPinClick, paper, ink, highlight]);

  return <div ref={containerRef} style={{ width: "100%", height: "100%", ...style }} />;
});

function buildMarkerElement({ color, paper, ink, live, highlight }: { color: string; paper: string; ink: string; live: boolean; highlight: boolean }) {
  const el = document.createElement("div");
  // IMPORTANT: do not set `position` here. MapLibre's `.maplibregl-marker`
  // class applies `position: absolute; top: 0; left: 0;` so that the
  // `transform: translate(...)` it writes on each marker positions it from
  // a shared origin. An inline `position: relative` would override that,
  // pushing markers into normal document flow — each pin would then be
  // offset by its flow position, and the offsets would shift whenever the
  // marker set or container reflowed, making pins drift across the map.
  // Children below use absolute positioning with explicit width/height on
  // this element, which works under MapLibre's `position: absolute` too.
  el.style.cssText = `width: ${highlight ? 26 : 20}px; height: ${highlight ? 26 : 20}px;`;
  const dot = document.createElement("div");
  dot.style.cssText = `
    position: absolute; inset: 0; border-radius: 50%;
    background: ${color}; border: 2.5px solid ${paper};
    box-shadow: 0 0 0 ${highlight ? 1.5 : 0}px ${ink}, 0 2px 6px rgba(0,0,0,0.25);
  `;
  el.appendChild(dot);
  if (live) {
    const ripple = document.createElement("div");
    ripple.style.cssText = `
      position: absolute; inset: -8px; border-radius: 50%;
      background: ${color}; opacity: 0.28;
      animation: maMapPulse 1.8s infinite;
    `;
    el.appendChild(ripple);
  }
  ensureMapStyleInjected();
  return el;
}

// "You are here" marker. Distinct visual from venue/event pins: small
// blue dot with a white halo and a softer ripple. Uses the same
// maMapPulse keyframes injected by `ensureMapStyleInjected`.
function buildUserLocationElement(): HTMLDivElement {
  const el = document.createElement("div");
  el.style.cssText = "width: 16px; height: 16px;";
  const dot = document.createElement("div");
  dot.style.cssText = `
    position: absolute; inset: 0; border-radius: 50%;
    background: #2f7fd1; border: 2.5px solid #ffffff;
    box-shadow: 0 0 0 1px rgba(20,12,4,0.18), 0 1px 4px rgba(0,0,0,0.35);
  `;
  el.appendChild(dot);
  const ripple = document.createElement("div");
  ripple.style.cssText = `
    position: absolute; inset: -10px; border-radius: 50%;
    background: #2f7fd1; opacity: 0.32;
    animation: maMapPulse 1.8s infinite;
  `;
  el.appendChild(ripple);
  ensureMapStyleInjected();
  return el;
}

function ensureMapStyleInjected() {
  if (!document.getElementById("ma-map-style")) {
    const s = document.createElement("style");
    s.id = "ma-map-style";
    s.textContent = `
      @keyframes maMapPulse {
        0%   { transform: scale(1); opacity: 0.32; }
        100% { transform: scale(2.4); opacity: 0; }
      }
      .maplibregl-popup-content {
        font-family: var(--ma-mono);
        font-size: 11px; font-weight: 600;
        letter-spacing: 0.04em;
        background: var(--ma-cream);
        color: var(--ma-ink);
        border: var(--ma-border) solid var(--ma-ink);
        border-radius: var(--ma-card-radius);
        padding: 8px 12px;
        box-shadow: 0 4px 14px rgba(0,0,0,0.18);
      }
      .maplibregl-popup-tip { border-top-color: var(--ma-ink) !important; }
      .maplibregl-ctrl-attrib {
        font-family: var(--ma-mono); font-size: 9px;
        background: rgba(245,236,214,0.85) !important;
      }
    `;
    document.head.appendChild(s);
  }
}
